import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { NatService } from 'src/app/services/nat.service';
import { ToastrService } from 'ngx-toastr';
import { ImportWizardService } from '@/app/pages/project/map/repository/import-wizard/import-wizard.service';
import { ImportWizardGeoSpatialService } from '@/app/pages/project/map/repository/import-wizard/components/geospatial/import-wizard-geospatial.service';
import { ImportWizardStorage, TabularColumns, ValidationErrorsInterface } from '@/app/pages/project/map/repository/import-wizard/import-wizard.storage';
import { SupersetService } from 'src/app/services/superset.service';
import { ProjectService } from 'src/app/services/projects.service';
import { Subscription as NatsSubscription } from 'nats.ws';

export type NatsResponseInterface = {
    status: string;
    rows_validated: number;
    total_rows: number;
    progress_percentage: number;
    errors_found: number;
    valid: boolean;
};

export interface ValidationStartOptions {
    importId: string;
    columns: TabularColumns[];
    page: number;
    pageSize: number;
}

@Injectable()
export class ImportWizardGeoSpatialValidationHandler implements OnDestroy {

    private readonly _progress$ = new Subject<number>();
    private readonly _subs = new Subscription();
    private _tableName: string | null = null;
    private _isProgressCompleted: boolean = false;
    private _natsSubs: NatsSubscription[] = [];

    readonly progress$: Observable<number> = this._progress$.asObservable();

    constructor(
        private readonly natsService: NatService,
        private readonly importWizardService: ImportWizardService,
        private readonly importWizardGeoSpatialService: ImportWizardGeoSpatialService,
        private readonly importWizardStorage: ImportWizardStorage,
        private readonly toastrService: ToastrService,
        private readonly supersetService: SupersetService,
        private readonly projectService: ProjectService
    ) {}

    /**
     * Deve ser chamado no ngOnInit do componente.
     * Subscreve o NATS imediatamente para não perder mensagens.
     */
    init(importId: string, page: number, pageSize: number): void {
        this._subscribeNats(importId, page, pageSize);
    }

    /**
     * Define o nome da tabela final para criação do DW.
     * @param tableName Nome da tabela (ex: _uploaded_geotable_...)
     */
    setTableName(tableName: string): void {
        this._tableName = tableName;
        console.log('[GeoSpatialValidationHandler] Table name set to:', tableName);
        
        // Se o progresso já chegou em 100%, cria o DW agora
        if (this._isProgressCompleted) {
            console.log('[GeoSpatialValidationHandler] Progress already completed, creating DW now.');
            this._createSupersetDW(tableName);
        }
    }

    /**
     * Subscreve o tópico de progresso da importação final.
     * @param importId ID do job de importação
     */
    subscribeToImportProgress(importId: string): void {
        const topic = `import_wizard.${importId}.progress`;
        this.natsService.subscribeToTopic(topic, (msg: string) => {
            this._handleImportProgressMessage(msg);
        }).then(sub => {
            if (sub) {
                this._natsSubs.push(sub);
            }
        });
    }

    /**
     * Deve ser chamado ao acionar a validação.
     * O NATS já está ouvindo desde o init().
     */
    start({ importId, columns, page, pageSize }: ValidationStartOptions): void {
        const sub = this.importWizardGeoSpatialService
            .postValidateImportWizard(importId)
            .subscribe({
                next: () => {
                    this.importWizardStorage.setDisplayColumns(columns);
                },
                error: (err) => {
                    console.error('[GeoSpatialValidationHandler] Erro ao iniciar validação:', err);
                },
            });

        this._subs.add(sub);
    }

    ngOnDestroy(): void {
        this._progress$.complete();
        this._subs.unsubscribe();
        this._natsSubs.forEach(sub => {
            try {
                sub.unsubscribe();
            } catch (err) {
                console.error('[GeoSpatialValidationHandler] Erro ao desinscrever NATS:', err);
            }
        });
        this._natsSubs = [];
    }

    // ─── Private ────────────────────────────────────────────────────────────────

    private _subscribeNats(importId: string, page: number, pageSize: number): void {
        const topic = `import_wizard.${importId}.validation`;
        this.natsService.subscribeToTopic(topic, (msg: string) => {
            this._handleNatsMessage(msg, importId, page, pageSize);
        }).then(sub => {
            if (sub) {
                this._natsSubs.push(sub);
            }
        });
    }

    private _handleNatsMessage(msg: string, importId: string, page: number, pageSize: number): void {
        try {
            const result: NatsResponseInterface = JSON.parse(msg);
            this._progress$.next(result.progress_percentage);

            if (result.progress_percentage === 100) {
                result.valid
                    ? this._handleSuccess()
                    : this._handleValidationErrors(importId, page, pageSize);
            }
        } catch (error) {
            console.error('[GeoSpatialValidationHandler] Erro ao processar mensagem NATS:', error);
        }
    }

    private _handleImportProgressMessage(msg: string): void {
        try {
            const result = JSON.parse(msg);
            console.log('[GeoSpatialValidationHandler] NATS message:', result);
            this._progress$.next(result.progress_percentage);

            if (Number(result.progress_percentage) === 100) {
                console.log('[GeoSpatialValidationHandler] Progress 100 reached.');
                this._isProgressCompleted = true;
                
                // Prioritize tableName from result (if backend is updated later), otherwise use stored _tableName
                const tableName = result.table_name || this._tableName;
                if (tableName) {
                    this._createSupersetDW(tableName);
                } else {
                    console.log('[GeoSpatialValidationHandler] Table name not available yet, waiting for setTableName.');
                }
            }
        } catch (error) {
            console.error('[GeoSpatialValidationHandler] Erro ao processar mensagem NATS de progresso:', error);
        }
    }

    private _createSupersetDW(tableName: string | null): void {
        console.log('[GeoSpatialValidationHandler] Attempting to create DW with tableName:', tableName);
        if (this.importWizardStorage.state().isShared) {
            console.log('[GeoSpatialValidationHandler] Import is shared, skipping DW creation');
            return;
        }

        this.projectService.projectId$.pipe(take(1)).subscribe(projectId => {
            console.log('[GeoSpatialValidationHandler] Project ID:', projectId);
            if (!tableName || !projectId) {
                console.warn('[GeoSpatialValidationHandler] Missing tableName or projectId', { tableName, projectId });
                return;
            }

            const finalTableName = tableName.includes('.') ? tableName : `public.${tableName}`;

            const data = {
                project_id: projectId,
                tables: [finalTableName]
            };

            this.supersetService.postCreateSupersetDWS(data).subscribe({
                next: () => {
                    console.log('[GeoSpatialValidationHandler] DW criado automaticamente com sucesso');
                },
                error: (err) => {
                    console.error('[GeoSpatialValidationHandler] Erro ao criar DW automaticamente:', err);
                },
            });
        });
    }

    private _handleSuccess(): void {
        this.toastrService.success('Importação validada com sucesso!');
        
        this.importWizardStorage.setNextStep();
    }

    private _handleValidationErrors(importId: string, page: number, pageSize: number): void {
        const sub = this.importWizardService
            .getImportWizardErrors(importId, page, pageSize)
            .subscribe({
                next: (data) => {
                    this.importWizardStorage.setValidationErrors({
                        items: data.items,
                        total: data.total,
                    } as ValidationErrorsInterface);

                    if (data.total > 0) {
                        this.toastrService.warning('Foram encontrados erros na importação!', '', {
                            closeButton: true,
                        });
                    }

                    this.importWizardStorage.setNextStep();
                },
                error: (error) => {
                    console.error('[GeoSpatialValidationHandler] Erro ao buscar erros de validação:', error);
                },
            });

        this._subs.add(sub);
    }
}
